import {PageState,DateRange,PublisherOrSiteOption,ProductRecord, CLinkOwnerOption, QueryFlags, DemandSourceRecord} from "../utils/types";
import {PlacementRecord,IdName,TrafficSourceResponseDto} from "../utils/types";

import COUNTRIES from "../components/autocompletes/countries/countries";
import StateHelper from "../utils/StateHelper";
import {getGroupByArray} from "../utils/misc";
import {TRAFFIC_TYPES_ARRAY} from "../utils/misc";
import { PagesHelper } from "./PagesHelper";
import dayjs from "../utils/dayjs";

/**
 * This class is the one responsible to transform fron string and basic types
 * to specific objects that will be handled by Dropdowns.
 */
export const FiltersInitialStateHelper = {

  getDateRangeState : function() : DateRange{
    const pageState : PageState = StateHelper.getPageState();
    const dateRange : DateRange = {
      dateStart : null,
      dateEnd :  null
    };

    dateRange.dateStart = pageState.dateStart ? pageState.dateStart : dayjs.utc();
    dateRange.dateEnd = pageState.dateEnd ? pageState.dateEnd : dayjs.utc();

    return dateRange;
  },

  getTrafficTypesState : function() : any[] {
    const pageState : PageState = StateHelper.getPageState();
    const defaultSelected : any[] = [];

    const trafficTypesArray = pageState.trafficTypes ? pageState.trafficTypes.split(",") : [];

    for (let i = 0; i < trafficTypesArray.length; i++) {
      const id = trafficTypesArray[i];
      for (let x = 0; x < TRAFFIC_TYPES_ARRAY.length; x++) {
        const element = TRAFFIC_TYPES_ARRAY[x];
        if (id === element.id){
          defaultSelected.push(element);
        }
      }
    }
    return defaultSelected;
  },

  getGroupByState : function(page :string,allowedGroupByFields : string[]) : any[] {
    const pageState : PageState = StateHelper.getPageState();
    const groupByReturned : IdName[] = [];
    const GROUP_BY_ARRAY = getGroupByArray(page,allowedGroupByFields);
    const groupByArray = pageState.groupBy ? pageState.groupBy.split(",") : [];

    for (let i = 0; i < groupByArray.length; i++) {
      const id = groupByArray[i];
      for (let x = 0; x < GROUP_BY_ARRAY.length; x++) {
        const element = GROUP_BY_ARRAY[x];
        if (id === element.id){
          groupByReturned.push(element);
        }
      }
    }

    return groupByReturned;
  },

  getCountriesState : function() : any[] {
    const baseState : PageState = StateHelper.getPageState();
    const defaultSelected : any[] = [];
    const countryIdsArray = baseState.countries ? baseState.countries.split(",") : [];

    for (let i = 0; i < countryIdsArray.length; i++) {
      const id = countryIdsArray[i];
      for (let x = 0; x < COUNTRIES.length; x++) {
        const element = COUNTRIES[x];
        if (id === element.id){
          defaultSelected.push(element);
        }
      }
    }
    return defaultSelected;
  },

  getTrafficSourceState : function(publishersArray : PublisherOrSiteOption[]) : PublisherOrSiteOption[] {
    const pageState : PageState = StateHelper.getPageState();
    const defaultSelected : PublisherOrSiteOption[] = [];

    // Handle publisherIds (all sites for those publishers)
    // When we have a publisherId, select ALL site options for that publisher
    if (pageState.publisherIds && pageState.publisherIds.length > 0) {
      pageState.publisherIds.forEach(pubId => {
        // Find ALL site options for this publisher
        const publisherSiteOptions = publishersArray.filter(opt => {
          const parts = opt.key.split(":");
          return parts.length > 1 && parts[0] === pubId;
        });

        if (publisherSiteOptions.length > 0) {
          // Add all sites for this publisher
          publisherSiteOptions.forEach(opt => defaultSelected.push(opt));
        } else {
          console.warn(`No site options found for publisherId: ${pubId}`);
        }
      });
    }

    // Handle siteIds (specific sites)
    if (pageState.siteIds && pageState.siteIds.length > 0) {
      pageState.siteIds.forEach(siteId => {
        // Find the option that matches this siteId
        // The key format is "publisherId:siteId"
        const siteOption = publishersArray.find(opt => {
          const parts = opt.key.split(":");
          return parts.length > 1 && parts[1] === siteId;
        });

        if (siteOption) {
          defaultSelected.push(siteOption);
        } else {
          console.warn(`Site option not found for siteId: ${siteId}`);
        }
      });
    }

    return defaultSelected;
  },


  getDemandSourcesState : function(demandSourcesResponse : DemandSourceRecord[]) : DemandSourceRecord[] {
    const pageState : PageState = StateHelper.getPageState();
    const selected : DemandSourceRecord[] = [];
    const demandSourceIdsArray : string[] = pageState.demandSourceIds ? pageState.demandSourceIds.split(",") : [];

    // Return empty array if demandSourcesResponse is null/undefined
    if (!demandSourcesResponse) {
      return selected;
    }

    for (let i = 0; i < demandSourceIdsArray.length; i++) {
      const id = demandSourceIdsArray[i];
      const demandSource:DemandSourceRecord = demandSourcesResponse.find((demandSource) => demandSource.demandSourceId === id);
      if (demandSource) {
        selected.push(demandSource);
      }
    }
    return selected;
  },
    
  getProductsState : function(productsResponse : ProductRecord[]) : ProductRecord[] {
    const pageState : PageState = StateHelper.getPageState();
    const selected : ProductRecord[] = [];
    const productIdsArray : string[] = pageState.productIds ? pageState.productIds.split(",") : [];

    for (let i = 0; i < productIdsArray.length; i++) {
      const id = productIdsArray[i];
      let publisher:ProductRecord = productsResponse.find((product) => product.id === id);
      if (!publisher) publisher = { id, name: id, productId: id, productName: id, icon: "", productPlatform: "" };
      selected.push(publisher);
    }
    return selected;
  },

  getPublisherPlacementsInitialState : function(trafficSourceResponseDto: TrafficSourceResponseDto) : PlacementRecord[] {
    const pageState : PageState = StateHelper.getPageState();
    const publisherPlacementsArray : PlacementRecord[] = [];

    // Use placementIds directly - no need to reconstruct
    const placementIds = pageState.placementIds ? pageState.placementIds : [];

    for (let i = 0; i < placementIds.length; i++) {
      const placementId = placementIds[i];
      // Create a placement record with the placementId
      const placement: PlacementRecord = {
        apptapPublisherId: "***",
        apptapSiteId: "***",
        publisherPlacementId: placementId,
        publisherPlacementName: placementId
      };
      publisherPlacementsArray.push(placement);
    }
    return publisherPlacementsArray;
  },

  getCLinkOwnersState : function(page :string, cLinksResponse : CLinkOwnerOption[]) : CLinkOwnerOption[] {
    const pageState : PageState = StateHelper.getPageState();
    const selected : CLinkOwnerOption[] = [];
    const cLinksIdsArray : string[] =  pageState.cLinks ? pageState.cLinks : [];

    cLinksIdsArray.forEach((cLinkOption : string) => {
      let cLink : CLinkOwnerOption = cLinksResponse.find((cLink : CLinkOwnerOption) => cLinkOption === PagesHelper.fromCLinkOwnerOptionToString(cLink));
      if (!cLink) {
        cLink = {
          id: cLinkOption,
          key: cLinkOption,
          record: cLinkOption,
          text: ""
        }
      }
      selected.push(cLink);
    });

    return selected;
  },

  getQueryFlagsState : function() : any[] {
    const pageState : PageState = StateHelper.getPageState();
    const defaultSelected : any[] = [];

    const queryFlagsArray = pageState.queryFlags ? pageState.queryFlags : [];

    for (let i = 0; i < queryFlagsArray.length; i++) {
      const queryFlag = queryFlagsArray[i];
      for (let x = 0; x < Object.values(QueryFlags).length; x++) {
        const element = Object.values(QueryFlags)[x];
        if (queryFlag === element){
          defaultSelected.push(element);
        }
      }
    }
    return defaultSelected;
  }
};